import type {
  CalculationEngine,
  CalculationEngineDependencies,
  CalculationExplanation,
  CalculationExplanationStep,
  EstimateAssumption,
  EstimateExclusion,
  EstimateWarning,
  EstimatorInput,
  EstimatorResult,
  Money,
  MoneyRange,
  ProjectType
} from "../types/index.js";
import { estimateContingencyPercent } from "./contingency.js";
import {
  buildInputDrivenWarnings,
  buildManualReviewWarning,
  buildPlaceholderPricingWarning,
  buildSamplePricingDataWarning
} from "./warnings.js";
import { resolveRegionalMultiplier } from "../regional/resolve.js";
import { getProjectCalculator } from "./rules/index.js";
import { accumulateIntoBreakdown, emptyBreakdown, sumBreakdown, roundBreakdownConsistently } from "./breakdown.js";
import { DEFAULT_ROUNDING_CONFIG, roundMoneyRange, type RoundingConfig } from "./rounding.js";
import { DEFAULT_PROJECT_THRESHOLDS, evaluateProjectThresholds, type ProjectThresholds } from "./thresholds.js";
import { buildInputDrivenExclusions, buildStandardExclusions } from "./exclusions.js";
import { validateEstimatorInputAtRuntime } from "./input-validation.js";
import {
  buildInputSummary,
  buildKitchenPlainLanguageSummary,
  buildBasementPlainLanguageSummary,
  buildBasementSuitePlainLanguageSummary,
  buildDeckPatioPlainLanguageSummary,
  buildBathroomPlainLanguageSummary,
  buildAdditionPlainLanguageSummary,
  buildWholeHomePlainLanguageSummary,
  buildInteriorRoomPlainLanguageSummary,
  isInteriorRoomProjectType
} from "./explanation.js";
import { evaluateInputCompleteness } from "./input-completeness.js";

function money(amount: number): Money {
  return { amount: Math.round(amount), currency: "CAD" };
}

function addMoney(a: Money, b: Money): Money {
  return money(a.amount + b.amount);
}

function scaleMoney(a: Money, percent: number): Money {
  return money((a.amount * percent) / 100);
}

/** Engine-level configuration, injected rather than hardcoded so a caller can override rounding/thresholds per deployment without forking the engine. */
export interface EngineOptions {
  roundingConfig?: RoundingConfig;
  thresholds?: Partial<Record<ProjectType, ProjectThresholds>>;
}

/**
 * Phase 2 calculation engine.
 *
 * Given a structured `EstimatorInput` and injected pricing dependencies,
 * computes a planning-level renovation budget using the Phase 1 pricing
 * database - see docs/calculation-engine.md for the full walkthrough.
 * High level flow:
 *
 *   1. Resolve the regional multiplier (postal -> city -> province ->
 *      national baseline - see core/src/regional/resolve.ts).
 *   2. If a project-specific pricing profile exists for this project type
 *      AND the catalog has line items for it (packages/core/src/calculation/rules/),
 *      run it: each pricing category is included only if it's actually
 *      relevant to the input (a selection was made, a work-type flag is
 *      "yes", etc) - a kitchen estimate never applies a basement-specific
 *      adjustment, because the rule modules are project-type-specific and
 *      the pricing catalog is filtered by project type from the start.
 *   3. Classify every computed line item into the four breakdown buckets
 *      (labour / materials / fixtures&appliances / permits&design&misc).
 *   4. Sum the buckets into a construction estimate, compute contingency
 *      separately against it, and add them for the planning budget.
 *   5. Check the (pre-rounding) typical construction estimate against
 *      configured project-specific sanity thresholds - flag for manual
 *      review rather than silently returning or hiding an implausible
 *      number.
 *   6. Round every dollar figure to sensible planning increments (never
 *      cents-level precision) - see core/src/calculation/rounding.ts.
 *   7. Build a machine-readable explanation (structured input facts +
 *      per-line-item steps), assumptions, and exclusions.
 *
 * When no pricing profile/data exists for a project type, the engine
 * falls back to the same honest zero-value placeholder behavior as
 * Phase 0/1 - see `isPlaceholderPricing`/`estimateConfidence` on the
 * result, and docs/calculation-engine.md's "what happens when there's no
 * data" section.
 */
export class ReferenceCalculationEngine implements CalculationEngine {
  constructor(private readonly options: EngineOptions = {}) {}

  calculate(input: EstimatorInput, deps: CalculationEngineDependencies): EstimatorResult {
    const now = (deps.now ?? (() => new Date()))();
    const roundingConfig = this.options.roundingConfig ?? DEFAULT_ROUNDING_CONFIG;
    const thresholdsConfig = this.options.thresholds ?? DEFAULT_PROJECT_THRESHOLDS;

    const warnings: EstimateWarning[] = [
      ...validateEstimatorInputAtRuntime(input),
      ...buildInputDrivenWarnings(input)
    ];
    const assumptions: EstimateAssumption[] = [];
    const exclusions: EstimateExclusion[] = [...buildStandardExclusions(), ...buildInputDrivenExclusions(input)];
    const explanationSteps: CalculationExplanationStep[] = [];

    const regionalResolution = resolveRegionalMultiplier(input.location, deps.regionalPricing);
    const regionalMultiplier = regionalResolution.multiplier;
    explanationSteps.push({
      stepId: "regional_multiplier",
      label: "Regional adjustment",
      detail: `Applied a ${regionalMultiplier}x regional multiplier for ${input.location.province}${
        input.location.city ? ", " + input.location.city : ""
      }, resolved at the ${regionalResolution.resolvedTier} level${
        regionalResolution.isFallback ? " (fell back from more specific data)" : ""
      }.`
    });

    const calculator = getProjectCalculator(input.projectType);
    const relevantItemsExist = deps.pricingCatalog.items.some((i) => i.projectType === input.projectType);

    let breakdown = emptyBreakdown();
    let hasComputed = false;

    if (calculator && relevantItemsExist) {
      const output = calculator(input, deps.pricingCatalog, regionalMultiplier);
      assumptions.push(...output.assumptions);
      exclusions.push(...output.exclusions);
      if (output.warnings) warnings.push(...output.warnings);

      for (const { item, result } of output.applied) {
        accumulateIntoBreakdown(breakdown, item, result);
        explanationSteps.push({
          stepId: item.id,
          label: item.subcategory ? `${item.category} (${item.subcategory})` : item.category,
          detail: `${item.id}: ${result.quantityApplied} x ${item.unit} at combined multiplier ${result.appliedMultiplier.combined.toFixed(3)} (complexity ${result.appliedMultiplier.complexity}, finish ${result.appliedMultiplier.finishLevel}, regional ${result.appliedMultiplier.regional}).`,
          amount: result.typical ?? undefined
        });
      }
      hasComputed = output.applied.length > 0;

      if (output.anchorMissing) {
        exclusions.push({
          code: "excludes_base_scope",
          message: "The base/anchor scope of work could not be sized (missing or invalid room size) and was excluded from this estimate."
        });
      }
    }

    const isPlaceholderCatalog = deps.pricingCatalog.meta.isPlaceholder;
    const noMethodologyOrData = !calculator || !relevantItemsExist;

    if (noMethodologyOrData) {
      warnings.unshift(buildPlaceholderPricingWarning());
    } else if (isPlaceholderCatalog) {
      warnings.unshift(buildSamplePricingDataWarning());
    }

    // --- Construction estimate, contingency, planning budget (raw, pre-rounding) ---
    const constructionEstimate: MoneyRange = sumBreakdown(breakdown);
    const contingencyPercent = estimateContingencyPercent(input);
    const contingencyAmount: MoneyRange = {
      low: scaleMoney(constructionEstimate.low, contingencyPercent),
      typical: scaleMoney(constructionEstimate.typical, contingencyPercent),
      high: scaleMoney(constructionEstimate.high, contingencyPercent)
    };
    const planningBudget: MoneyRange = {
      low: addMoney(constructionEstimate.low, contingencyAmount.low),
      typical: addMoney(constructionEstimate.typical, contingencyAmount.typical),
      high: addMoney(constructionEstimate.high, contingencyAmount.high)
    };

    explanationSteps.push({
      stepId: "contingency",
      label: "Contingency",
      detail: `Recommended contingency set to ${contingencyPercent}% of the construction estimate, based on project risk factors (scope, structural/plumbing/electrical unknowns, permit status).`,
      amount: contingencyAmount.typical
    });

    // --- Sanity threshold check (on the raw, pre-rounding typical figure) ---
    let requiresManualReview = false;
    if (hasComputed) {
      const thresholdEval = evaluateProjectThresholds(input.projectType, constructionEstimate.typical.amount, thresholdsConfig);
      if (!thresholdEval.withinRange) {
        requiresManualReview = true;
        warnings.push(buildManualReviewWarning(thresholdEval.reason ?? ""));
      }
    }

    // --- Round everything for output - never false precision ---
    // Bucket + total rounding is done together (not via four independent
    // roundMoneyRange calls) so the breakdown always sums to the headline
    // estimate - see roundBreakdownConsistently's doc comment (Phase 20 audit fix).
    const { roundedBreakdown, roundedTotal: roundedEstimate } = roundBreakdownConsistently(breakdown, constructionEstimate, roundingConfig);
    // The planning budget is rounded independently from its own true (pre-rounding)
    // value - same reasoning as the total above, it's the authoritative headline
    // figure for "estimate + contingency". The displayed contingency amount is then
    // DERIVED as the difference between the two already-rounded figures, rather than
    // rounded independently from its own true value - that's what guarantees
    // `estimate + contingency === planningBudget` exactly, at low/typical/high, instead
    // of drifting by a rounding increment the way three independent roundMoneyRange
    // calls could (Phase 20 audit fix - same class of bug as the breakdown buckets,
    // just one level up). Every tier's increment is itself a multiple of the smallest
    // (250), so this difference always lands on the increment grid too - never false
    // cents-level precision.
    const roundedPlanningBudget = roundMoneyRange(planningBudget, roundingConfig);
    const roundedContingencyAmount: MoneyRange = {
      low: money(roundedPlanningBudget.low.amount - roundedEstimate.low.amount),
      typical: money(roundedPlanningBudget.typical.amount - roundedEstimate.typical.amount),
      high: money(roundedPlanningBudget.high.amount - roundedEstimate.high.amount)
    };

    const genericSummary = hasComputed
      ? `Estimate computed from ${explanationSteps.length - 2} applicable pricing line item(s) for this project - irrelevant categories for this project type/selections were not applied.`
      : "No pricing methodology or pricing data is available yet for this project type - returned a zero-value placeholder result rather than a fabricated number.";

    // Ensuite shares bathroom's entire cost model/question set (see rules/ensuite.ts and
    // explanation.ts's buildBathroomPlainLanguageSummary doc comment), so it shares its summary too.
    const bathroomSummary = input.projectType === "bathroom" || input.projectType === "ensuite";
    const explanation: CalculationExplanation = {
      summary: hasComputed
        ? input.projectType === "kitchen"
          ? buildKitchenPlainLanguageSummary(input)
          : input.projectType === "basement"
            ? buildBasementPlainLanguageSummary(input)
            : input.projectType === "basement_suite"
              ? buildBasementSuitePlainLanguageSummary(input)
              : input.projectType === "deck_patio"
                ? buildDeckPatioPlainLanguageSummary(input)
                : bathroomSummary
                  ? buildBathroomPlainLanguageSummary(input)
                  : input.projectType === "addition"
                    ? buildAdditionPlainLanguageSummary(input)
                    : input.projectType === "whole_home_renovation"
                      ? buildWholeHomePlainLanguageSummary(input)
                      : isInteriorRoomProjectType(input.projectType)
                        ? buildInteriorRoomPlainLanguageSummary(input)
                        : genericSummary
        : genericSummary,
      inputSummary: buildInputSummary(input, regionalResolution),
      steps: explanationSteps
    };

    const completeness = evaluateInputCompleteness(input, assumptions);

    return {
      requestId: input.requestId,
      generatedAt: now.toISOString(),
      estimate: roundedEstimate,
      recommendedContingencyPercent: contingencyPercent,
      recommendedContingencyAmount: roundedContingencyAmount,
      planningBudget: roundedPlanningBudget,
      breakdown: roundedBreakdown,
      explanation,
      assumptions,
      exclusions,
      warnings,
      pricingVersion: deps.pricingCatalog.meta.pricingVersion,
      pricingDate: deps.pricingCatalog.meta.effectiveDate,
      estimateConfidence: !hasComputed ? "low" : isPlaceholderCatalog ? "medium" : "high",
      isPlaceholderPricing: noMethodologyOrData || isPlaceholderCatalog,
      requiresManualReview,
      inputCompleteness: completeness.level,
      inputCompletenessNotes: completeness.improvementNotes
    };
  }
}

export function createCalculationEngine(options: EngineOptions = {}): CalculationEngine {
  return new ReferenceCalculationEngine(options);
}
